// src/components/AppRouter.js

import React, { useEffect, useRef, useState } from 'react';
import {
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// ============================================================
// ERROR BOUNDARY
// ============================================================
import {
  NavigationTracker,
  withScreenErrorBoundary,
} from './EnhancedErrorBoundary';

// ============================================================
// ADMIN APP CONTENT
// ============================================================
import AdminAppContent from './AdminAppContent';

// ============================================================
// USER / PROVIDER APP CONTENT
// ============================================================
import AppContent from './AppContent';

// ============================================================
// ADMIN AUTH
// ============================================================
import {
  selectIsAuthenticated as selectAdminIsAuthenticated,
  selectAuthLoading as selectAdminLoading,
  adminCheckAuth,
  resetAuthState,
  clearErrors,
} from '../store/slices/adminSlice';

// ============================================================
// USER AUTH
// ============================================================
import {
  selectIsAuthenticated as selectUserIsAuthenticated,
  selectAuthLoading as selectUserLoading,
  checkAuth,
} from '../store/slices/authSlice';

// ============================================================
// LOADING SCREEN
// ============================================================

const LoadingScreen = () => {
  return (
    <div className="loading-container">
      <div className="loading-content">
        <div className="spinner" />
        <p>Initializing application...</p>
      </div>
    </div>
  );
};

// ============================================================
// ADMIN ERROR WRAPPER
// ============================================================

const AdminApplication = withScreenErrorBoundary(
  AdminAppContent,
  'AdminApp'
);

// ============================================================
// USER / PROVIDER ERROR WRAPPER
// ============================================================

const UserApplication = withScreenErrorBoundary(
  AppContent,
  'UserApp'
);

// ============================================================
// APP ROUTER
// ============================================================

const AppRouter = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [appReady, setAppReady] = useState(false);

  // Prevent authentication initialization from running
  // repeatedly when Redux authentication state changes.
  const authInitializedRef = useRef(false);

  // ==========================================================
  // ADMIN AUTH STATE
  // ==========================================================

  const isAdminAuthenticated = useSelector(
    selectAdminIsAuthenticated
  );

  const adminLoading = useSelector(
    selectAdminLoading
  );

  // ==========================================================
  // USER AUTH STATE
  // ==========================================================

  const isUserAuthenticated = useSelector(
    selectUserIsAuthenticated
  );

  const userLoading = useSelector(
    selectUserLoading
  );

  // ==========================================================
  // DETERMINE ROUTE TYPE
  // ==========================================================

  const isAdminRoute =
    location.pathname === '/admin' ||
    location.pathname.startsWith('/admin/');

  // ==========================================================
  // INITIAL AUTHENTICATION CHECK
  // ==========================================================

  useEffect(() => {
    let mounted = true;

    // --------------------------------------------------------
    // Do not initialize authentication more than once.
    // --------------------------------------------------------

    if (authInitializedRef.current) {
      if (mounted) {
        setAppReady(true);
      }

      return () => {
        mounted = false;
      };
    }

    authInitializedRef.current = true;

    const initializeAuth = async () => {
      try {
        // ====================================================
        // ADMIN ROUTE
        // ====================================================

        if (isAdminRoute) {
          if (
            !isAdminAuthenticated &&
            !adminLoading
          ) {
            try {
              await dispatch(
                adminCheckAuth()
              ).unwrap();
            } catch (error) {
              // No valid admin session.
              //
              // IMPORTANT:
              // Do not navigate here.
              // AdminAppContent is responsible for
              // displaying the correct admin login page.
              dispatch(resetAuthState());
              dispatch(clearErrors());
            }
          }
        }

        // ====================================================
        // USER / PROVIDER ROUTE
        // ====================================================

        else {
          if (
            !isUserAuthenticated &&
            !userLoading
          ) {
            try {
              await dispatch(
                checkAuth()
              ).unwrap();
            } catch (error) {
              // No user session.
              //
              // Do not redirect here.
              // AppContent / its route guards should decide
              // what should happen.
            }
          }
        }
      } catch (error) {
        // Prevent authentication initialization errors
        // from crashing the application.
        console.error('Auth initialization error:', error);
      } finally {
        if (mounted) {
          setAppReady(true);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, [
    // ✅ Only include values that change and affect the effect
    dispatch,
    isAdminRoute,
    isAdminAuthenticated,
    adminLoading,
    isUserAuthenticated,
    userLoading,
  ]);

  // ==========================================================
  // INITIAL LOADING
  // ==========================================================

  if (!appReady) {
    return <LoadingScreen />;
  }

  // ==========================================================
  // APPLICATION ROUTES
  // ==========================================================

  return (
    <>
      {/* ----------------------------------------------------
          Keep EnhancedErrorBoundary route information
          synchronized with React Router.
      ---------------------------------------------------- */}
      <NavigationTracker />

      <Routes>

        {/* ==================================================
            ADMIN APPLICATION

            Examples:

            /admin
            /admin/login
            /admin/register
            /admin/dashboard
            /admin/providers
            /admin/users
        ================================================== */}

        <Route
          path="/admin/*"
          element={<AdminApplication />}
        />

        {/* ==================================================
            USER / PROVIDER APPLICATION

            Everything that is NOT /admin goes here.

            Examples:

            /
            /login
            /register
            /provider
            /provider/dashboard
            /provider-business-verification
        ================================================== */}

        <Route
          path="/*"
          element={<UserApplication />}
        />

      </Routes>
    </>
  );
};

export default AppRouter;